import { ChangeEvent } from 'react';
import {
  IFilterCount,
  IFilterDrawerSchema,
  IOption,
  IResetSelectedOptions,
  ISectionedOption,
  ISelectedOptions
} from './types';
import {
  DATE_QUERY_PARAMS,
  FieldInteractionStatusesFilterKey,
  FilterKeys,
  FiltersWithSubfiltersMapping,
  TeleInteractionStatusesFilterKey
} from './constants';
import { getTodayDate } from '@cp/src/pages/CaseDetails/constants/communicationHistory.constant';
import { _map } from '@cp/src/utils/commonUtils';

export const removeSpecificOption = (
  filterKeyOptionsMap: ISelectedOptions,
  filterKey: string,
  option = 'ALL'
) => {
  const index = filterKeyOptionsMap?.[filterKey]?.findIndex(element => element === option);
  if (index != -1) filterKeyOptionsMap?.[filterKey]?.splice(index, 1);
  return filterKeyOptionsMap;
};

export const resetSelectedOptions = ({
  deleteFilterKey = '',
  clearAll = false,
  deleteOption = undefined,
  filterSchemaMap,
  queryParams,
  setSelectedOptions
}: IResetSelectedOptions) => {
  const updatedSelectedOptionsList = filterSchemaMap.reduce((selected, filterKey) => {
    const isNestedFilter =
      queryParams?.[filterKey] &&
      !Array.isArray(queryParams?.[filterKey]) &&
      typeof queryParams?.[filterKey] === 'object';
    if (isNestedFilter) {
      const nestedFilters = Object.keys(queryParams?.[filterKey] || {});
      nestedFilters.forEach(nestedFilter => {
        selected[filterKey] = {
          ...(selected[filterKey] || {}),
          [nestedFilter]:
            filterKey === deleteFilterKey || clearAll
              ? []
              : queryParams?.[filterKey]?.[nestedFilter] || []
        };
      });
      return { ...selected };
    }

    const shouldClear = filterKey === deleteFilterKey || clearAll;
    return {
      ...selected,
      [filterKey]: shouldClear ? [] : queryParams?.[filterKey] || []
    };
  }, {});

  const resetValueWithoutALLOption =
    deleteOption && Object.keys(deleteOption).length
      ? removeSpecificOption(updatedSelectedOptionsList, deleteOption?.filter, deleteOption?.option)
      : updatedSelectedOptionsList;
  setSelectedOptions(resetValueWithoutALLOption);
  return resetValueWithoutALLOption;
};

export const isOptionChecked = (
  option: string,
  selectedOptions: ISelectedOptions,
  selectedFilter: string
): boolean => {
  const index = selectedOptions?.[selectedFilter]?.findIndex(data => data === option);
  return index != -1;
};

export const handleOptionChange = (
  e: ChangeEvent<HTMLInputElement>,
  option: IOption,
  filterKey: string,
  selectedOptions: ISelectedOptions,
  filterSchema: Record<string, IFilterDrawerSchema>,
  setSelectedOptions: (selectedOptions: ISelectedOptions) => void
) => {
  const checked = e?.target?.checked;
  let selectedOptionFilterMap = { ...selectedOptions };

  if (DATE_QUERY_PARAMS.includes(filterKey as FilterKeys)) {
    selectedOptionFilterMap[filterKey] = [option.value as string];
    setSelectedOptions(selectedOptionFilterMap);
    return;
  }

  if (checked) {
    if (filterSchema[filterKey].multiSelect) {
      selectedOptionFilterMap?.[filterKey]?.push(option.value as string);
    } else {
      selectedOptionFilterMap[filterKey] = [option.value as string];
    }
  } else {
    selectedOptionFilterMap = removeSpecificOption(
      selectedOptionFilterMap,
      filterKey,
      option.value as string
    );
  }
  setSelectedOptions(selectedOptionFilterMap);
};

export const getFilterKey = (selectedFilter: string, selectedOption: string) => {
  if (selectedFilter === FilterKeys.TELE_INTERACTION_STATUSES) {
    return TeleInteractionStatusesFilterKey.get(selectedOption as FiltersWithSubfiltersMapping);
  }
  return FieldInteractionStatusesFilterKey.get(selectedOption as FiltersWithSubfiltersMapping);
};

export const getOptionsForSectionedFilters = (options: ISectionedOption[], key: string) => {
  const filterSectionValues = options.map(option => option.filterSectionValue).flat();
  return { options: filterSectionValues, label: key };
};

export const getOptionsAndLabels = (
  filterSchema: Record<string, IFilterDrawerSchema>,
  filterKey: string
) => {
  return filterSchema[filterKey].options?.[0]?.filterSectionLabel
    ? getOptionsForSectionedFilters(filterSchema[filterKey].options, filterSchema[filterKey].label)
    : filterSchema[filterKey] || {};
};

export const getCurrentDate = () => {
  return getTodayDate().toISOString().slice(0, 10);
};

export const countNestedFilters = (activeFilters: any): number => {
  let count = 0;
  _map(activeFilters, nestedFilterKey => {
    const nestedValue = activeFilters?.[nestedFilterKey]?.[0];
    if (nestedValue) {
      count += 1;
    }
  });
  return count;
};

export const countRegularFilters = (activeFilters: any): number => {
  if (activeFilters && Array.isArray(activeFilters)) {
    return activeFilters.length;
  } else if (activeFilters) {
    return 1;
  }
  return 0;
};

export const createFilterText = (filterCounts: IFilterCount[]): string => {
  return filterCounts.map(({ label, count }) => `${label}(${count})`).join(', ');
};
